// =====================================================================
// 3Dconnexion driver mode (WAMP) -- the driver treats this page as a real 3D
// viewport, same as its own THREE.js sample: it reads our camera/model state
// and writes the new camera matrix back directly.
// See https://3dconnexion.com/technical_support/web_threejs.html
// =====================================================================
// Model is in millimeters. Report raw camera/model coordinates (same as the
// sample) and tell the driver the unit size via model.unitsToMeters.
// Do NOT also pre-scale positions — that double-applies and kills pan/zoom.
import * as THREE from 'three';
import { isPointClipped } from './geometry';
import { clamp } from './math';
import type { WampMessage, WampState } from './types';
import { dom, log, setDeviceName, setStatus } from './ui';
import {
  camera,
  getModelBoundingBox,
  getModelGroup,
  getModelRadius,
  isOrthographic,
  keepRenderingWhile,
  onFrame,
  PERSPECTIVE_FOV_DEG,
  pivot,
  raycaster,
  renderer,
  requestRender,
  updateCameraClipping
} from './viewer';

const UNITS_TO_METERS = 0.001;
const WAMP_HOST = '127.51.68.120';
const WAMP_DISCOVERY_PORT = 8181;
const WAMP_FALLBACK_PORTS = [8182, 8181];
// Match 3dconnexion.min.js (version "0.7.0" → controller version 0.7)
const WAMP_JS_VERSION = '0.7.0';
const WAMP_APP_NAME = '3D Viewer';

interface PendingCall {
  resolve: (value: any) => void;
  reject: (reason: Error) => void;
}

let wampState: WampState = 'idle';
let wampSocket: WebSocket | null = null;
let wampPendingCalls: Record<string, PendingCall> = {};
let wampControllerUri: string | null = null;
let wampHasFocus = false;
let wampAnimating = false;
let wampCoastUntil = 0;
let wampSkipAffine = 0;
let wampEmaPosDelta = 0;
let wampEmaAngDelta = 0;
/** Last applied camera translation; used to drop release spring-back packs. */
const wampLastMove = new THREE.Vector3();
/** Scroll-wheel zoom while the cap is held — kept across driver affine writes. */
let userViewDist: number | null = null;
let lastRawDriverDist = 0;
const _userDollyDir = new THREE.Vector3();
let wampFrameTime = 0;
let wampMotionStats: MotionStats | null = null;
let hitOrigin: THREE.Vector3 | null = null;
let hitDirection: THREE.Vector3 | null = null;
let lastDegenerateLog = 0;
// Sample tracks the mouse globally so pointer.position can be answered outside
// of an event (used for pivot-at-cursor).
let lastMouseX = 0;
let lastMouseY = 0;

export function getWampState(): WampState {
  return wampState;
}

export function isWampConnected(): boolean {
  return wampState === 'connected';
}

export function isWampAnimating(): boolean {
  return wampAnimating;
}

function clearUserViewDistance(): void {
  userViewDist = null;
  lastRawDriverDist = 0;
}

/**
 * After scroll-zoom during SpaceMouse motion, remember the camera↔pivot
 * distance so the next view.affine write cannot undo it. SpaceMouse zoom
 * still blends in via the raw driver distance ratio.
 */
export function retainViewDistanceAfterUserZoom(): void {
  if (!wampAnimating) return;
  userViewDist = Math.max(camera.position.distanceTo(pivot), 1e-6);
}

function reapplyUserViewDistance(): void {
  if (userViewDist === null) return;
  const rawDist = Math.max(camera.position.distanceTo(pivot), 1e-6);
  if (lastRawDriverDist > 1e-6) {
    userViewDist *= rawDist / lastRawDriverDist;
  }
  lastRawDriverDist = rawDist;
  const modelRadius = getModelRadius();
  userViewDist = clamp(userViewDist, modelRadius * 0.02, modelRadius * 80);
  _userDollyDir.subVectors(camera.position, pivot);
  if (_userDollyDir.lengthSq() < 1e-12) return;
  _userDollyDir.normalize();
  camera.position.copy(pivot).addScaledVector(_userDollyDir, userViewDist);
  camera.updateMatrixWorld(true);
  updateCameraClipping();
}

function setWampState(next: WampState): void {
  wampState = next;
}

// ---------------------------------------------------------------------------
// Axis diagnostics overlay: decodes each incoming camera update into which way
// the camera is moving (in view space) and rotating, so axis directions can be
// verified against physical SpaceMouse input instead of guessed.
// ---------------------------------------------------------------------------

let axisDiagOn = false;
let axisDiagPrevPos: THREE.Vector3 | null = null;
let axisDiagPrevQuat: THREE.Quaternion | null = null;
let axisDiagPeak = { mx: 0, my: 0, mz: 0, rx: 0, ry: 0, rz: 0 };

export function isAxisDiagOn(): boolean {
  return axisDiagOn;
}

export function toggleAxisDiag(): void {
  axisDiagOn = !axisDiagOn;
  dom.axisDiag.classList.toggle('hidden', !axisDiagOn);
  dom.axisDiagToggle.textContent = axisDiagOn ? 'Axis diagnostics off' : 'Axis diagnostics on';
  axisDiagPrevPos = null;
  axisDiagPrevQuat = null;
  axisDiagPeak = { mx: 0, my: 0, mz: 0, rx: 0, ry: 0, rz: 0 };
  if (axisDiagOn) dom.axisDiag.textContent = 'Move the SpaceMouse in one direction…';
}

function renderAxisDiag(sections: Array<{ heading: string; rows: string[] }>): void {
  dom.axisDiag.textContent = '';
  sections.forEach((section, sectionIndex) => {
    const heading = document.createElement('b');
    heading.textContent = section.heading;
    dom.axisDiag.appendChild(heading);
    dom.axisDiag.appendChild(document.createTextNode('\n' + section.rows.join('\n')));
    if (sectionIndex < sections.length - 1) dom.axisDiag.appendChild(document.createTextNode('\n'));
  });
}

function updateAxisDiag(driverPos: THREE.Vector3, quat: THREE.Quaternion): void {
  if (!axisDiagOn) return;
  if (axisDiagPrevPos && axisDiagPrevQuat) {
    // Positional delta expressed in the CURRENT view basis (right/up/fwd)
    const dWorld = driverPos.clone().sub(axisDiagPrevPos);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(quat);
    const mx = dWorld.dot(right), my = dWorld.dot(up), mz = dWorld.dot(fwd);

    // Rotational delta as an axis-angle
    const dq = quat.clone().multiply(axisDiagPrevQuat.clone().invert());
    dq.normalize();
    const angle = 2 * Math.acos(Math.min(1, Math.abs(dq.w)));
    const s = Math.sqrt(1 - dq.w * dq.w);
    let rx = 0, ry = 0, rz = 0;
    if (s > 1e-6) { const sign = dq.w < 0 ? -1 : 1; rx = sign * dq.x / s * angle; ry = sign * dq.y / s * angle; rz = sign * dq.z / s * angle; }

    const p = axisDiagPeak;
    if (Math.abs(mx) > Math.abs(p.mx)) p.mx = mx;
    if (Math.abs(my) > Math.abs(p.my)) p.my = my;
    if (Math.abs(mz) > Math.abs(p.mz)) p.mz = mz;
    if (Math.abs(rx) > Math.abs(p.rx)) p.rx = rx;
    if (Math.abs(ry) > Math.abs(p.ry)) p.ry = ry;
    if (Math.abs(rz) > Math.abs(p.rz)) p.rz = rz;

    const f = (v: number) => (v >= 0 ? '+' : '') + v.toFixed(4);
    const dir = (v: number, pos: string, neg: string) => Math.abs(v) < 1e-5 ? '·' : (v > 0 ? pos : neg);
    renderAxisDiag([
      {
        heading: 'Beweging (view-as)',
        rows: [
          'links/rechts:  ' + f(mx) + '  ' + dir(mx, 'RECHTS', 'LINKS'),
          'omhoog/omlaag: ' + f(my) + '  ' + dir(my, 'OMHOOG', 'OMLAAG'),
          'in/uit (zoom): ' + f(mz) + '  ' + dir(mz, 'IN', 'UIT')
        ]
      },
      {
        heading: 'Rotatie (rad)',
        rows: [
          'pitch (X): ' + f(rx),
          'yaw   (Y): ' + f(ry),
          'roll  (Z): ' + f(rz)
        ]
      }
    ]);
  }
  axisDiagPrevPos = driverPos.clone();
  axisDiagPrevQuat = quat.clone();
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

function wampRandId(len: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = '';
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// Native code tends to crash on NaN/Infinity where JS would just carry on --
// so nothing goes over the wire without a hard finite-number check, regardless
// of which camera-math edge case produced it.
function wampSanitize(value: any): any {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (Array.isArray(value)) return value.map(wampSanitize);
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const k in value) out[k] = wampSanitize(value[k]);
    return out;
  }
  return value;
}

function wampSend(arr: WampMessage): void {
  if (!(wampSocket && wampSocket.readyState === WebSocket.OPEN)) return;
  try {
    wampSocket.send(JSON.stringify(wampSanitize(arr)));
  } catch (err) {
    log('WAMP: could not send message: ' + (err instanceof Error ? err.message : String(err)), true);
  }
}

function wampCall(procUri: string, args: any[]): Promise<any> {
  return new Promise((resolve, reject) => {
    const callId = '0.' + wampRandId(12).toLowerCase();
    wampPendingCalls[callId] = { resolve, reject };
    wampSend([2, callId, procUri, ...args]);
  });
}

// Sample: while the SpaceMouse is moving, push frame.time every rAF so the
// driver can apply motion (required when frame.timingSource === 1).
function wampPushFrame(time: number): void {
  if (!(wampSocket && wampSocket.readyState === WebSocket.OPEN && wampControllerUri)) return;
  if (!wampHasFocus) return;
  wampFrameTime = time;
  const callId = '0.' + wampRandId(12).toLowerCase();
  wampSend([2, callId, '3dx_rpc:update', wampControllerUri, { frame: { time } }]);
}

// Mirror 3dconnexion.min.js focus/blur: release the device when this page is
// not the foreground app so Inventor / other CAD can take over.
export function wampSetFocus(focused: boolean): void {
  focused = !!focused;
  if (!(wampSocket && wampSocket.readyState === WebSocket.OPEN && wampControllerUri)) return;
  if (wampHasFocus === focused) return;
  wampHasFocus = focused;
  const callId = '0.' + wampRandId(12).toLowerCase();
  wampSend([2, callId, '3dx_rpc:update', wampControllerUri, { focus: focused }]);
  if (!focused) {
    wampAnimating = false;
    wampCoastUntil = 0;
  }
  const connected = isWampConnected();
  setStatus(connected ? (focused ? 'live' : 'idle') : 'idle',
    connected
      ? (focused ? 'Connected' : 'SpaceMouse idle (another app focused)')
      : 'SpaceMouse not connected');
}

export function syncWampFocusFromDocument(): void {
  const focused = document.visibilityState === 'visible' && document.hasFocus();
  wampSetFocus(focused);
}

async function discoverWampPort(): Promise<number | null> {
  const url = `https://${WAMP_HOST}:${WAMP_DISCOVERY_PORT}/3dconnexion/nlproxy`;
  try {
    const res = await fetch(url, { mode: 'cors' });
    const info = await res.json();
    if (info && info.port) {
      log('WAMP: nlproxy reports port ' + info.port + ' (version ' + info.version + ')', true);
      return info.port;
    }
  } catch (err) {
    log('WAMP: could not query nlproxy (' + (err instanceof Error ? err.message : String(err)) + ')', true);
  }
  return null;
}

function openWampSocket(url: string): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const socket = new WebSocket(url, 'wamp');
    const timer = setTimeout(() => { if (!settled) { settled = true; socket.close(); reject(new Error('timeout')); } }, 2500);
    socket.onopen = () => { if (!settled) { settled = true; clearTimeout(timer); resolve(socket); } };
    socket.onerror = () => { if (!settled) { settled = true; clearTimeout(timer); reject(new Error('connection error')); } };
    socket.onclose = ev => { if (!settled) { settled = true; clearTimeout(timer); reject(new Error('closed before open (code ' + ev.code + ')')); } };
  });
}

export async function connectWamp(): Promise<void> {
  // One connection attempt at a time: the auto-connect on boot, the manual
  // button and the reconnect after loading a file can otherwise overlap and
  // leave two sockets fighting over the controller.
  if (wampState === 'discovering' || wampState === 'connecting' || wampState === 'connected') {
    log('WAMP: connection attempt already in progress (' + wampState + '), ignored', true);
    return;
  }

  setWampState('discovering');
  setStatus('idle', 'Looking up port…');
  const discovered = await discoverWampPort();
  const ports = discovered ? [discovered, ...WAMP_FALLBACK_PORTS.filter(p => p !== discovered)] : WAMP_FALLBACK_PORTS;

  setWampState('connecting');
  for (const port of ports) {
    const url = `wss://${WAMP_HOST}:${port}/`;
    setStatus('idle', 'Connecting on port ' + port + '…');
    log('WAMP: trying ' + url, true);
    try {
      const socket = await openWampSocket(url);
      wampSocket = socket;
      attachWampHandlers(socket);
      return;
    } catch (err) {
      log('WAMP: port ' + port + ' failed (' + (err instanceof Error ? err.message : String(err)) + ')', true);
    }
  }
  setWampState('failed');
  setStatus('err', 'Driver unreachable');
  log('WAMP: no port worked', true);
}

function attachWampHandlers(socket: WebSocket): void {
  socket.onopen = () => log('WAMP: websocket open', true);
  socket.onerror = () => { setStatus('err', 'Driver unreachable'); log('WAMP: connection error during session', true); };
  socket.onclose = ev => {
    const wasWamp = isWampConnected();
    log('WAMP: connection closed (code ' + ev.code + ')', true);
    if (wasWamp) {
      wampTeardown(true);
    } else {
      setWampState('idle');
      setStatus('idle', 'SpaceMouse not connected');
    }
  };
  socket.onmessage = ev => {
    try { handleWampMessage(JSON.parse(ev.data)); }
    catch (err) { log('WAMP: could not parse message: ' + (err instanceof Error ? err.message : String(err)), true); }
  };
}

function setConnectedUi(connected: boolean, label: string): void {
  setWampState(connected ? 'connected' : 'idle');
  dom.connectWampBtn.disabled = connected;
  dom.disconnectBtn.disabled = !connected;
  setDeviceName(connected ? label : '');
  setStatus(connected ? 'live' : 'idle', connected ? 'Connected' : 'SpaceMouse not connected');
}

async function handleWampMessage(msg: WampMessage): Promise<void> {
  const type = msg[0];
  if (type === 0) {
    log('WAMP: welcome (' + msg[3] + ')', true);
    wampSend([1, '3dx_rpc', 'wss://127.51.68.120/3dconnexion#']);
    wampSend([1, '3dconnexion', 'wss://127.51.68.120/3dconnexion']);
    wampSend([1, 'self', location.href]);
    try {
      const mouseResult = await wampCall('3dx_rpc:create', ['3dconnexion:3dmouse', WAMP_JS_VERSION]);
      const mouseId = mouseResult.connexion;
      // Exact same controller options as 3dconnexion.min.js create3dmouse()
      const ctrlResult = await wampCall('3dx_rpc:create', ['3dconnexion:3dcontroller', mouseId, {
        version: parseFloat(WAMP_JS_VERSION),
        name: WAMP_APP_NAME,
        rowMajorOrder: false
      }]);
      wampControllerUri = '3dconnexion:3dcontroller/' + ctrlResult.instance;
      wampSend([5, wampControllerUri]);
      // Sample: app is the animation-frame timing source. Claim focus only if
      // this window is actually in the foreground.
      const startFocused = document.visibilityState === 'visible' && document.hasFocus();
      wampHasFocus = false; // force wampSetFocus to send
      await wampCall('3dx_rpc:update', [wampControllerUri, {
        focus: startFocused,
        frame: { timingSource: 1 }
      }]);
      wampHasFocus = startFocused;
      if (dom.canvas && typeof dom.canvas.focus === 'function') {
        try { dom.canvas.setAttribute('tabindex', '0'); if (startFocused) dom.canvas.focus(); } catch { /* ignore */ }
      }
      setConnectedUi(true, '3Dconnexion driver');
      if (!startFocused) setStatus('idle', 'SpaceMouse idle (another app focused)');
      log('WAMP: controller active — ' + wampControllerUri + ' ("' + WAMP_APP_NAME + '", unitsToMeters=' + UNITS_TO_METERS + ', v' + WAMP_JS_VERSION + ')', true);
    } catch (err) {
      setWampState('failed');
      setStatus('err', 'Driver refused the connection');
      log('WAMP: setup failed: ' + (err instanceof Error ? err.message : String(err)), true);
      if (wampSocket) wampSocket.close();
    }
  } else if (type === 3) {
    const [, callId, result] = msg;
    const pending = wampPendingCalls[callId];
    if (pending) { delete wampPendingCalls[callId]; pending.resolve(result); }
  } else if (type === 4) {
    const [, callId, errorUri, desc] = msg;
    const pending = wampPendingCalls[callId];
    if (pending) { delete wampPendingCalls[callId]; pending.reject(new Error(errorUri + ': ' + desc)); }
  } else if (type === 8) {
    const [, topic, payload] = msg;
    if (topic !== wampControllerUri) return;
    handleWampControllerRpc(payload);
  }
}

let lastGoodAffineArray: number[] | null = null;

// Identity converters: sample hands the driver raw world coordinates.
function toDriverPos(v: THREE.Vector3): THREE.Vector3 { return v.clone(); }
function toWorldPos(v: THREE.Vector3): THREE.Vector3 { return v.clone(); }

function currentAffineArray(): number[] {
  camera.updateMatrixWorld(true);
  const arr = camera.matrixWorld.toArray();
  if (!arr.every(Number.isFinite)) {
    log('WAMP: invalid camera matrix, restoring last known good pose', true);
    const fallback = lastGoodAffineArray || [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 10, 1];
    const m = new THREE.Matrix4().fromArray(fallback);
    const scaleVec = new THREE.Vector3();
    m.decompose(camera.position, camera.quaternion, scaleVec);
    camera.scale.copy(scaleVec);
    camera.updateMatrixWorld(true);
    return fallback.slice();
  }
  lastGoodAffineArray = arr.slice();
  return arr;
}

// Sample getFov: diagonal FOV in radians (three.js stores vertical degrees).
function diagonalFovRad(): number {
  const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
  const halfVert = (PERSPECTIVE_FOV_DEG * Math.PI / 180) / 2;
  return 2 * Math.atan(Math.tan(halfVert) * Math.sqrt(1 + aspect * aspect));
}

interface MotionStats {
  maxDPos: number;
  maxDPosRow: number;
  maxDAng: number;
  affineCount: number;
  props: Record<string, number>;
  prevPos: THREE.Vector3 | null;
  prevPosRow: THREE.Vector3 | null;
  prevQuat: THREE.Quaternion | null;
}

function beginMotionStats(): void {
  wampMotionStats = {
    maxDPos: 0,
    maxDPosRow: 0,
    maxDAng: 0,
    affineCount: 0,
    props: {},
    prevPos: null,
    prevPosRow: null,
    prevQuat: null
  };
}

function noteMotionProp(name: string): void {
  if (!wampMotionStats) return;
  wampMotionStats.props[name] = (wampMotionStats.props[name] || 0) + 1;
}

function endMotionStats(): void {
  const s = wampMotionStats;
  wampMotionStats = null;
  if (!s || !axisDiagOn) return;
  const propList = Object.keys(s.props).map(k => k + '=' + s.props[k]).join(', ') || '(no updates)';
  log(
    'WAMP motion: |Δpos|col=' + s.maxDPos.toFixed(4) +
    ' |Δpos|row=' + s.maxDPosRow.toFixed(4) +
    ' |Δrot|=' + s.maxDAng.toFixed(4) +
    ' affine=' + s.affineCount +
    ' [' + propList + ']',
    true
  );
}

/** Camera writes are only applied while the cap is actively held. */
function wampAcceptsCameraWrites(): boolean {
  return wampHasFocus && wampAnimating;
}

// Soft-clamp a sudden camera jump (large dt after idle, or a bad pack).
// Caps stay distance-relative so zoomed-in views cannot teleport by model size.
// EMA may raise the cap a little, but never above a fraction of view distance —
// otherwise a fast move leaves a high EMA that lets the release spring-back
// through as a visible jump.
function applyAffineSmoothed(testPos: THREE.Vector3, testQuat: THREE.Quaternion, testScale: THREE.Vector3): void {
  const prevPos = camera.position.clone();
  const prevQuat = camera.quaternion.clone();
  const dPos = testPos.distanceTo(prevPos);
  const dq = testQuat.clone().multiply(prevQuat.clone().invert()).normalize();
  const dAng = 2 * Math.acos(Math.min(1, Math.abs(dq.w)));

  const viewDist = Math.max(prevPos.distanceTo(pivot), getModelRadius() * 0.02, 1e-3);
  const maxPos = Math.max(viewDist * 0.18, Math.min(wampEmaPosDelta * 2.5, viewDist * 0.35), 0.05);
  const maxAng = Math.max(0.08, Math.min(wampEmaAngDelta * 2.5, 0.25), 0.02);

  // Reject strong reversals vs the recent move direction (cap spring-back while
  // motion is still flagged true for a frame or two).
  if (wampLastMove.lengthSq() > 1e-8 && dPos > viewDist * 0.04) {
    const move = testPos.clone().sub(prevPos);
    if (move.dot(wampLastMove) < -0.2 * move.length() * wampLastMove.length()) {
      wampEmaPosDelta *= 0.5;
      wampEmaAngDelta *= 0.5;
      return;
    }
  }

  let t = 1;
  if (dPos > maxPos && dPos > 1e-9) t = Math.min(t, maxPos / dPos);
  if (dAng > maxAng && dAng > 1e-9) t = Math.min(t, maxAng / dAng);

  if (t < 1) {
    testPos.lerpVectors(prevPos, testPos, t);
    testQuat.slerpQuaternions(prevQuat, testQuat, t);
  }

  const appliedPos = testPos.distanceTo(prevPos);
  const appliedDq = testQuat.clone().multiply(prevQuat.clone().invert()).normalize();
  const appliedAng = 2 * Math.acos(Math.min(1, Math.abs(appliedDq.w)));
  wampEmaPosDelta = wampEmaPosDelta * 0.8 + appliedPos * 0.2;
  wampEmaAngDelta = wampEmaAngDelta * 0.8 + appliedAng * 0.2;
  if (appliedPos > 1e-8) {
    wampLastMove.copy(testPos).sub(prevPos);
  }

  camera.position.copy(testPos);
  camera.quaternion.copy(testQuat);
  camera.scale.copy(testScale);
  // Avoid non-uniform / near-zero scales from a bad affine packing the driver.
  if (!(testScale.x > 0.5 && testScale.y > 0.5 && testScale.z > 0.5)) {
    camera.scale.set(1, 1, 1);
  }
  // Keep scroll-wheel zoom while the driver keeps writing affines.
  reapplyUserViewDistance();
  camera.updateMatrixWorld(true);
  updateCameraClipping();
  requestRender();
}

function handleWampControllerRpc(payload: any[]): void {
  const callId = payload[1];
  const method = payload[2];
  const propName = payload[4];
  const value = payload[5];

  if (method === 'self:read') {
    switch (propName) {
      // Sample getViewMatrix: column-major camera.matrixWorld
      case 'view.affine': wampSend([3, callId, currentAffineArray()]); break;
      case 'view.perspective': wampSend([3, callId, !isOrthographic()]); break;
      case 'view.rotatable': wampSend([3, callId, true]); break;
      case 'view.constructionPlane': wampSend([3, callId, [0, 1, 0, 0]]); break;
      // Sample getViewFrustum: near-plane frustum from vertical fov
      case 'view.frustum': {
        if (camera instanceof THREE.OrthographicCamera) {
          wampSend([3, callId, [
            camera.left, camera.right, camera.bottom, camera.top, camera.near, camera.far
          ]]);
        } else {
          const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
          const tanHalf = Math.tan((PERSPECTIVE_FOV_DEG * Math.PI / 180) / 2);
          const bottom = -camera.near * tanHalf;
          const left = bottom * aspect;
          wampSend([3, callId, [left, -left, bottom, -bottom, camera.near, camera.far]]);
        }
        break;
      }
      case 'view.extents':
        if (camera instanceof THREE.OrthographicCamera) {
          wampSend([3, callId, [
            camera.left, camera.right, camera.bottom, camera.top
          ]]);
        } else {
          wampSend([4, callId, 'self:read#generic', 'view.extents not used in perspective']);
        }
        break;
      case 'view.fov':
        if (isOrthographic()) {
          wampSend([4, callId, 'self:read#generic', 'view.fov not used in orthographic']);
        } else {
          wampSend([3, callId, diagonalFovRad()]);
        }
        break;
      // Sample does not implement getViewTarget
      case 'view.target':
        wampSend([4, callId, 'self:read#generic', 'view.target unknown property']);
        break;
      case 'model.extents': {
        const box = getModelBoundingBox();
        if (box) {
          const mn = toDriverPos(box.min);
          const mx = toDriverPos(box.max);
          wampSend([3, callId, [mn.x, mn.y, mn.z, mx.x, mx.y, mx.z]]);
        } else {
          wampSend([3, callId, [-1, -1, -1, 1, 1, 1]]);
        }
        break;
      }
      // Sample getFloorPlane: Y-up ground through the origin
      case 'model.floorPlane': wampSend([3, callId, [0, 1, 0, 0]]); break;
      case 'model.unitsToMeters':
      case 'unitsToMeters':
        wampSend([3, callId, UNITS_TO_METERS]);
        break;
      case 'selection.empty': wampSend([3, callId, true]); break;
      case 'selection.affine': wampSend([3, callId, null]); break;
      case 'selection.extents': wampSend([3, callId, null]); break;
      case 'views.front': wampSend([3, callId, [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]]); break;
      case 'coordinateSystem': wampSend([3, callId, [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]]); break;
      case 'pivot.position': wampSend([3, callId, toDriverPos(pivot).toArray()]); break;
      case 'pivot.visible': wampSend([3, callId, true]); break;
      case 'frame.timingSource': wampSend([3, callId, 1]); break;
      case 'frame.time': wampSend([3, callId, wampFrameTime || performance.now()]); break;
      // Sample getPointerPosition: mouse unprojected onto the near plane
      case 'pointer.position': {
        const rect = dom.canvas.getBoundingClientRect();
        const ndc = new THREE.Vector3(
          ((lastMouseX - rect.left) / rect.width) * 2 - 1,
          -((lastMouseY - rect.top) / rect.height) * 2 + 1,
          -1
        );
        camera.updateProjectionMatrix();
        camera.updateMatrixWorld(true);
        ndc.unproject(camera);
        wampSend([3, callId, toDriverPos(ndc).toArray()]);
        break;
      }
      case 'hit.lookat': {
        const modelGroup = getModelGroup();
        if (hitOrigin && hitDirection && modelGroup) {
          raycaster.set(hitOrigin, hitDirection.clone().normalize());
          raycaster.far = camera.far;
          const hits = raycaster.intersectObject(modelGroup, true);
          // Same as picking: the driver should look at what is on screen, not
          // at what the section cut away.
          const hit = hits.find(h => !isPointClipped(h.point, renderer.clippingPlanes));
          if (hit) { wampSend([3, callId, toDriverPos(hit.point).toArray()]); break; }
        }
        wampSend([3, callId, null]);
        break;
      }
      default: wampSend([4, callId, 'self:read#generic', propName + ' unknown property']);
    }
  } else if (method === 'self:update') {
    if (propName === 'view.affine' && Array.isArray(value) && value.length === 16 && value.every(Number.isFinite)) {
      noteMotionProp('view.affine');
      // Freeze the view as soon as the cap is released. The old coast window
      // still applied late affine packs — that is exactly when the physical
      // spring-back shows up as a visible jump (worse when zoomed in).
      // We still push frame.time during coast for the driver; we just don't move.
      if (!wampAcceptsCameraWrites()) {
        wampSend([3, callId, {}]);
        return;
      }
      if (wampSkipAffine > 0) {
        wampSkipAffine--;
        wampSend([3, callId, {}]);
        return;
      }
      // Sample setViewMatrix: decompose column-major matrix onto the camera
      const m = new THREE.Matrix4().fromArray(value);
      const testPos = new THREE.Vector3(), testQuat = new THREE.Quaternion(), testScale = new THREE.Vector3();
      m.decompose(testPos, testQuat, testScale);
      const rowPos = new THREE.Vector3(value[3], value[7], value[11]);
      const decomposed = [testPos.x, testPos.y, testPos.z, testQuat.x, testQuat.y, testQuat.z, testQuat.w];
      const scaleOk = testScale.x > 1e-6 && testScale.y > 1e-6 && testScale.z > 1e-6;
      if (wampMotionStats) {
        wampMotionStats.affineCount++;
        if (wampMotionStats.prevPos && wampMotionStats.prevPosRow && wampMotionStats.prevQuat) {
          wampMotionStats.maxDPos = Math.max(wampMotionStats.maxDPos, testPos.distanceTo(wampMotionStats.prevPos));
          wampMotionStats.maxDPosRow = Math.max(wampMotionStats.maxDPosRow, rowPos.distanceTo(wampMotionStats.prevPosRow));
          const dq = testQuat.clone().multiply(wampMotionStats.prevQuat.clone().invert());
          const ang = 2 * Math.acos(Math.min(1, Math.abs(dq.w)));
          wampMotionStats.maxDAng = Math.max(wampMotionStats.maxDAng, ang);
        }
        wampMotionStats.prevPos = testPos.clone();
        wampMotionStats.prevPosRow = rowPos.clone();
        wampMotionStats.prevQuat = testQuat.clone();
      }
      if (decomposed.every(Number.isFinite) && scaleOk) {
        applyAffineSmoothed(testPos, testQuat, testScale);
        updateAxisDiag(camera.position, camera.quaternion);
      } else if (performance.now() - lastDegenerateLog > 1000) {
        lastDegenerateLog = performance.now();
        log('WAMP: ontvangen view.affine degenereert (schaal ~0 of NaN), ignored', true);
      }
    } else if (propName === 'view.fov' && Number.isFinite(value) && value > 0.01 && value < Math.PI) {
      noteMotionProp('view.fov');
      // Perspective FOV is fixed in this viewer; ignore driver writes.
    } else if (propName === 'view.extents') {
      noteMotionProp('view.extents');
      if (
        wampAcceptsCameraWrites() &&
        camera instanceof THREE.OrthographicCamera &&
        Array.isArray(value) &&
        value.length >= 4 &&
        value.every(Number.isFinite)
      ) {
        camera.left = value[0];
        camera.right = value[1];
        camera.bottom = value[2];
        camera.top = value[3];
        camera.updateProjectionMatrix();
        requestRender();
      }
    } else if (propName === 'view.target' && Array.isArray(value) && value.length >= 3 && value.every(Number.isFinite)) {
      noteMotionProp('view.target');
      if (wampAcceptsCameraWrites()) {
        const newTarget = toWorldPos(new THREE.Vector3(value[0], value[1], value[2]));
        const delta = newTarget.clone().sub(pivot);
        pivot.copy(newTarget);
        camera.position.add(delta);
        updateCameraClipping();
        requestRender();
      }
    } else if (propName === 'pivot.position' && Array.isArray(value) && value.length >= 3 && value.every(Number.isFinite)) {
      noteMotionProp('pivot.position');
      if (wampAcceptsCameraWrites()) {
        const newPivot = toWorldPos(new THREE.Vector3(value[0], value[1], value[2]));
        const delta = newPivot.clone().sub(pivot);
        const viewDist = Math.max(camera.position.distanceTo(pivot), getModelRadius() * 0.02, 1e-3);
        const maxPivotStep = Math.max(viewDist * 0.25, Math.min(wampEmaPosDelta * 3, viewDist * 0.4), 0.05);
        if (delta.length() > maxPivotStep) delta.setLength(maxPivotStep);
        pivot.add(delta);
        // Keep camera framed on the pivot when the driver pans via pivot alone
        camera.position.add(delta);
        if (delta.lengthSq() > 1e-8) wampLastMove.copy(delta);
        updateCameraClipping();
        requestRender();
      }
    } else if (propName === 'pivot.visible') {
      noteMotionProp('pivot.visible');
    } else if (propName === 'hit.lookfrom' && Array.isArray(value) && value.length >= 3 && value.every(Number.isFinite)) {
      hitOrigin = toWorldPos(new THREE.Vector3(value[0], value[1], value[2]));
    } else if (propName === 'hit.direction' && Array.isArray(value) && value.length >= 3 && value.every(Number.isFinite)) {
      hitDirection = new THREE.Vector3(value[0], value[1], value[2]);
    } else if (propName === 'hit.aperture') {
      // aperture is accepted but not used by this viewer
    } else if (propName === 'hit.selectionOnly') {
      // ignored
    } else if (propName === 'motion') {
      if (value) {
        beginMotionStats();
        wampAnimating = true;
        wampCoastUntil = 0;
        wampEmaPosDelta = 0;
        wampEmaAngDelta = 0;
        wampLastMove.set(0, 0, 0);
        clearUserViewDistance();
        // Sync frame clock so the first step has a normal dt (avoids sprint)
        wampPushFrame(performance.now());
        wampSkipAffine = 2;
        requestRender();
      } else {
        endMotionStats();
        wampAnimating = false;
        wampEmaPosDelta = 0;
        wampEmaAngDelta = 0;
        wampLastMove.set(0, 0, 0);
        clearUserViewDistance();
        // Feed frame.time briefly for driver bookkeeping only — camera is frozen.
        wampCoastUntil = performance.now() + 120;
        requestRender();
      }
    } else if (propName === 'transaction') {
      noteMotionProp('transaction');
    } else {
      noteMotionProp(propName);
    }
    wampSend([3, callId, {}]);
  } else {
    wampSend([4, callId, method + '#generic', 'not implemented']);
  }
}

export function wampTeardown(fromClose: boolean): void {
  if (wampControllerUri && wampSocket && wampSocket.readyState === WebSocket.OPEN) {
    try {
      const callId = '0.' + wampRandId(12).toLowerCase();
      wampSend([2, callId, '3dx_rpc:update', wampControllerUri, { focus: false }]);
    } catch { /* ignore */ }
  }
  if (!fromClose && wampSocket) { try { wampSocket.close(); } catch { /* ignore */ } }
  wampSocket = null;
  wampControllerUri = null;
  wampPendingCalls = {};
  wampHasFocus = false;
  wampAnimating = false;
  wampCoastUntil = 0;
  wampSkipAffine = 0;
  wampEmaPosDelta = 0;
  wampEmaAngDelta = 0;
  wampLastMove.set(0, 0, 0);
  clearUserViewDistance();
  wampFrameTime = 0;
  wampMotionStats = null;
  setConnectedUi(false, '');
}

// Keep feeding frame.time during motion and briefly after release so the
// driver can decelerate instead of dumping a leftover velocity spike.
onFrame(time => {
  if (wampAnimating || performance.now() < wampCoastUntil) wampPushFrame(time);
});
keepRenderingWhile(() => wampAnimating || performance.now() < wampCoastUntil);

export function initWampInput(): void {
  document.addEventListener('mousemove', e => { lastMouseX = e.clientX; lastMouseY = e.clientY; });

  // Release / reclaim the SpaceMouse when switching apps or browser tabs
  window.addEventListener('focus', () => wampSetFocus(true));
  window.addEventListener('blur', () => wampSetFocus(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') wampSetFocus(false);
    else syncWampFocusFromDocument();
  });
  // Same as the sample: viewport focus/blur
  dom.canvas.setAttribute('tabindex', '0');
  dom.canvas.addEventListener('focus', () => wampSetFocus(true));
  dom.canvas.addEventListener('blur', () => {
    // Only release if the whole window also lost focus (clicking UI in-page
    // blurs the canvas but should keep the SpaceMouse on this app).
    setTimeout(() => {
      if (!document.hasFocus() || document.visibilityState === 'hidden') wampSetFocus(false);
    }, 0);
  });
  window.addEventListener('pointerdown', () => {
    if (isWampConnected() && document.hasFocus()) wampSetFocus(true);
  });
}
